/* eslint-disable @typescript-eslint/no-empty-function */
import { FormInstance } from 'antd/lib'
import React, { useState, useContext, Dispatch, SetStateAction, useEffect } from 'react'

type ContextState = {
  ticketToSearch?: string | undefined
  setTicketToSearch: Dispatch<SetStateAction<string | undefined>>
  maxDaysRange: number
  unreadNotifications: number
  setUnreadNotifications: Dispatch<SetStateAction<number>>
  menuDisplayed: boolean
  handleDisplayMenu: () => void
  filtersDisplayed: boolean
  handleDisplayFilters: () => void
  formInstance: FormInstance | undefined
  setFormInstance: Dispatch<SetStateAction<FormInstance | undefined>>
}

interface GeneralProviderProps {
  children: React.ReactNode
}

const initialState = {
  ticketToSearch: undefined,
  setTicketToSearch: () => {},
  maxDaysRange: 14,
  unreadNotifications: 0,
  setUnreadNotifications: () => {},
  isLoadingAllCarriers: false,
  menuDisplayed: false,
  handleDisplayMenu: () => {},
  filtersDisplayed: false,
  handleDisplayFilters: () => {},
  formInstance: undefined,
  setFormInstance: () => {},
}

const GeneralContext = React.createContext<ContextState>(initialState)

export function GeneralProvider({ children }: GeneralProviderProps) {
  const [ticketToSearch, setTicketToSearch] = useState<string | undefined>(undefined)
  const [unreadNotifications, setUnreadNotifications] = useState<number>(0)
  const { maxDaysRange } = initialState

  const getInitialMenuState = () => {
    if (typeof window !== 'undefined') {
      return window.innerWidth > 768
    }
    return true
  }

  const [menuDisplayed, setMenuDisplayed] = useState(getInitialMenuState())
  const [filtersDisplayed, setFiltersDisplayed] = useState(false)
  const [formInstance, setFormInstance] = useState<FormInstance>()

  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth <= 768
      if (isMobile && menuDisplayed) {
        setMenuDisplayed(false)
      }
      if (!isMobile && !menuDisplayed) {
        setMenuDisplayed(true)
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [menuDisplayed])

  const handleDisplayMenu = () => {
    if (filtersDisplayed) {
      setFiltersDisplayed(false)
    }
    setMenuDisplayed(!menuDisplayed)
  }

  const handleDisplayFilters = () => {
    if (menuDisplayed) {
      setMenuDisplayed(false)
    }
    setFiltersDisplayed(!filtersDisplayed)
  }

  return (
    <GeneralContext.Provider
      value={{
        ticketToSearch,
        setTicketToSearch,
        maxDaysRange,
        unreadNotifications,
        setUnreadNotifications,
        menuDisplayed,
        handleDisplayMenu,
        filtersDisplayed,
        handleDisplayFilters,
        formInstance,
        setFormInstance,
      }}
    >
      {children}
    </GeneralContext.Provider>
  )
}

export function useGeneral(): ContextState {
  const context = useContext(GeneralContext)

  if (!context) throw Error('This hook can only be used under the FiltersProvider component')
  return context
}
